import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LuPlus } from 'react-icons/lu';
import { FaUserCircle } from 'react-icons/fa';
import { updateProfileImage } from '../../redux/features/authSlice';
import { useChat } from '../../chat/ChatProvider';
import ChatNotificationButton from './ChatNotificationButton';
import DropdownSignOutItem from './DropdownSignOutItem';
import {
  NavIconHome,
  NavIconBrowse,
  NavIconMap,
  NavIconPricing,
  NavIconMegaphone,
  NavIconBlog,
} from './NavDropdownIcons';
import {
  NavbarWrapper,
  NavbarContent,
  NavbarLeftSection,
  NavbarSearchSlot,
  NavbarProfile,
  NavbarActions,
  NavbarActionsDivider,
  ProfileIcon,
  DropdownMenu,
  DropdownList,
  DropdownItem,
  NavbarSecondaryButton,
  TooltipWrapper,
  Tooltip,
} from '../../styles/navigation/NavbarStyles';
import DashboardSidebarToggle from '../sidebars/DashboardSidebarToggle';
import DashboardOmnibarSearch from './DashboardOmnibarSearch';
import { fetchInfluencerProfile } from '../../hooks/influencer/useInfluencerProfile';
import useHideCreatorMapEntryPoints from '../../hooks/navigation/useHideCreatorMapEntryPoints';
import { CREATOR_MAP_PATH } from '../../utils/navigation/creatorMapVisibility';

function Navbar() {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);
  const lastFetchRef = useRef(0);
  const PROFILE_CACHE_DURATION = 60000; // 60 seconds cache
  const hideCreatorMapEntryPoints = useHideCreatorMapEntryPoints();

  const userType = useSelector((state) => state.auth.userType);
  const profileImageUrl = useSelector((state) => state.auth.profileImageUrl);

  const chatContext = useChat();
  const toggleChat = chatContext?.toggle || (() => {});
  const unreadCount = chatContext?.unreadCount || 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownVisible && !event.target.closest('.navbar-profile-container')) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);

  useEffect(() => {
    const fetchProfileData = async () => {
      const token = localStorage.getItem('token');
      if (!token || !userType) return;

      // Throttle: Don't call if we've called recently
      const now = Date.now();
      if (hasFetchedRef.current && (now - lastFetchRef.current) < PROFILE_CACHE_DURATION) {
        return;
      }

      try {
        if (userType === 'brand') {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/brand/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            lastFetchRef.current = now;
            hasFetchedRef.current = true;
            // Only update Redux if we don't already have it (prevents loop)
            if (data.brand_logo_url && !profileImageUrl) {
              dispatch(updateProfileImage(data.brand_logo_url));
            }
          }
          return;
        }

        if (userType === 'influencer') {
          const data = await fetchInfluencerProfile(false);
          lastFetchRef.current = now;
          hasFetchedRef.current = true;
          // For influencers, use the profile_picture_url field directly
          // Only update Redux if we don't already have it (prevents loop)
          if (data?.profile_picture_url && !profileImageUrl) {
            dispatch(updateProfileImage(data.profile_picture_url));
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]); // Removed profileImageUrl and dispatch from deps to prevent loop

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const handleProfileNavigate = (path) => {
    window.location.href = path;
    setDropdownVisible(false);
  };

  const handlePostCampaign = () => {
    window.location.href = '/dashboard/campaigns/create';
  };

  const handleSignOut = async () => {
    try {
      localStorage.clear();
      setDropdownVisible(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getDropdownItemClassName = (path, isExact = false) => {
    const currentPath = window.location?.pathname || '';
    const isActive = isExact ? currentPath === path : currentPath.startsWith(path);
    return isActive ? 'active' : '';
  };

  const renderDropdownItems = () => {
    if (userType === 'influencer') {
      return (
        <>
          <DropdownItem
            className={getDropdownItemClassName('/', true)}
            onClick={() => handleProfileNavigate('/')}
          >
            <NavIconHome className="dropdown-icon" />
            Home
          </DropdownItem>
          <DropdownItem
            className={getDropdownItemClassName('/marketplace/campaigns')}
            onClick={() => handleProfileNavigate('/marketplace/campaigns')}
          >
            <NavIconMegaphone className="dropdown-icon" />
            Campaigns
          </DropdownItem>
          <DropdownItem
            className={getDropdownItemClassName('/blog')}
            onClick={() => handleProfileNavigate('/blog')}
          >
            <NavIconBlog className="dropdown-icon" />
            Blog
          </DropdownItem>
          <DropdownSignOutItem onSignOut={handleSignOut} />
        </>
      );
    }

    return (
      <>
        <DropdownItem
          className={getDropdownItemClassName('/', true)}
          onClick={() => handleProfileNavigate('/')}
        >
          <NavIconHome className="dropdown-icon" />
          Home
        </DropdownItem>
        <DropdownItem
          className={getDropdownItemClassName('/marketplace/influencers')}
          onClick={() => handleProfileNavigate('/marketplace/influencers')}
        >
          <NavIconBrowse className="dropdown-icon" />
          Browse Influencers
        </DropdownItem>
        {!hideCreatorMapEntryPoints && (
          <DropdownItem
            className={getDropdownItemClassName(CREATOR_MAP_PATH, true)}
            onClick={() => handleProfileNavigate(CREATOR_MAP_PATH)}
          >
            <NavIconMap className="dropdown-icon" />
            Influencer Map
          </DropdownItem>
        )}
        <DropdownItem
          className={getDropdownItemClassName('/blog')}
          onClick={() => handleProfileNavigate('/blog')}
        >
          <NavIconBlog className="dropdown-icon" />
          Blog
        </DropdownItem>
        <DropdownItem
          className={getDropdownItemClassName('/pricing', true)}
          onClick={() => handleProfileNavigate('/pricing')}
        >
          <NavIconPricing className="dropdown-icon" />
          Pricing
        </DropdownItem>
        <DropdownSignOutItem onSignOut={handleSignOut} />
      </>
    );
  };

  return (
    <NavbarWrapper>
      <NavbarContent>
        <NavbarLeftSection>
          <DashboardSidebarToggle userType={userType} />
        </NavbarLeftSection>
        <NavbarSearchSlot>
          <DashboardOmnibarSearch userType={userType} />
        </NavbarSearchSlot>
        <NavbarProfile className="navbar-profile-container">
          <ChatNotificationButton toggleChat={toggleChat} unreadCount={unreadCount} />

          {userType === 'brand' && (
            <NavbarActions>
              <TooltipWrapper>
                <NavbarSecondaryButton
                  $accent
                  onClick={handlePostCampaign}
                  aria-label="Post new campaign"
                  type="button"
                >
                  <span className="navbar-action-btn-icon">
                    <LuPlus aria-hidden strokeWidth={1.75} />
                  </span>
                </NavbarSecondaryButton>
                <Tooltip>Post Campaign</Tooltip>
              </TooltipWrapper>
            </NavbarActions>
          )}

          <NavbarActionsDivider aria-hidden />
          <TooltipWrapper $alignRight $suppress={dropdownVisible}>
            <ProfileIcon onClick={toggleDropdown} aria-label="Open navigation menu" role="button" tabIndex={0}>
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt=""
                  className="profile-picture"
                  onError={(e) => {
                    console.error('Failed to load profile image');
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <FaUserCircle className="profile-icon-svg" />
              )}
            </ProfileIcon>
            <Tooltip $alignRight>Navigation</Tooltip>
          </TooltipWrapper>
          <DropdownMenu $visible={dropdownVisible}>
            <DropdownList>
              {renderDropdownItems()}
            </DropdownList>
          </DropdownMenu>
        </NavbarProfile>
      </NavbarContent>
    </NavbarWrapper>
  );
}

export default Navbar;
